
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { ImageUpload } from "@/components/ui/image-upload"
import { VideoUpload } from "../components/ui/video-upload"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { courseService } from "@/services/course.service"
import { useToast } from "@/hooks/use-toast"
import { ArrowLeft, Plus, Edit2, Trash2, GripVertical, X, Loader2 } from "lucide-react"
import type { Course, Category, Author, Theme, Lesson } from "@/types/api"
import { LessonDialog } from "@/components/courses/LessonDialog"
import { Badge } from "@/components/ui/badge"

const formSchema = z.object({
    title: z.string().min(5, "Название должно содержать не менее 5 символов"),
    slug: z.string().min(2, "Слаг должен содержать не менее 2 символов").max(50, "Слаг не должен превышать 50 символов").regex(/^[a-z0-9-]+$/, "Слаг может содержать только английские буквы (a-z), цифры и дефисы"),
    description: z.string().min(10, "Описание должно содержать не менее 10 символов"),
    banner: z.any().optional(),
    banner_portrait: z.any().optional(),
    trailer_type: z.enum(["youtube", "file"]).default("file"),
    trailer_video: z.any().optional(),
    trailer_video_file: z.any().optional(),
    learning_outcomes: z.array(z.object({
        title: z.string().min(1, "Название обязательно"),
        text: z.string().min(10, "Текст должен содержать не менее 10 символов"),
    })).optional().nullable(),
    price: z.string().optional().nullable(),
    is_active: z.boolean().default(false),
    is_upcoming: z.boolean().default(false),
    release_date: z.string().optional().nullable(),
    category_ids: z.array(z.number()).min(1, "Выберите хотя бы одну категорию"),
    author_ids: z.array(z.number()).min(1, "Выберите хотя бы одного автора"),
    theme_ids: z.array(z.number()).optional(),
})

export default function CourseEditPage() {
    const { slug } = useParams()
    const navigate = useNavigate()
    const { toast } = useToast()
    const isEditMode = !!slug

    const [course, setCourse] = useState<Course | null>(null)
    const [categories, setCategories] = useState<Category[]>([])
    const [authors, setAuthors] = useState<Author[]>([])
    const [themes, setThemes] = useState<Theme[]>([])
    const [lessons, setLessons] = useState<Lesson[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    // Lesson Dialog State
    const [editLesson, setEditLesson] = useState<Lesson | null>(null)
    const [isLessonDialogOpen, setIsLessonDialogOpen] = useState(false)
    const [deleteLessonId, setDeleteLessonId] = useState<number | null>(null)

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "",
            slug: "",
            description: "",
            banner: null,
            banner_portrait: null,
            trailer_type: "file",
            trailer_video: "",
            trailer_video_file: null,
            learning_outcomes: [],
            price: null,
            is_active: false,
            is_upcoming: false,
            release_date: null,
            category_ids: [],
            author_ids: [],
            theme_ids: [],
        },
    })

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "learning_outcomes",
    })

    useEffect(() => {
        const initData = async () => {
            setIsLoading(true)
            try {
                // Fetch relations
                const [catsRes, authsRes, themesRes] = await Promise.all([
                    courseService.getCategories({ limit: 100 }),
                    courseService.getAuthors({ limit: 100 }),
                    courseService.getThemes({ limit: 100 }),
                ])
                setCategories(catsRes.results)
                setAuthors(authsRes.results)
                setThemes(themesRes.results)

                if (isEditMode && slug) {
                    const courseData = await courseService.getCourse(slug)
                    setCourse(courseData)
                    setLessons(courseData.lessons || [])

                    form.reset({
                        title: courseData.title,
                        slug: courseData.slug,
                        description: courseData.description || "",
                        banner: courseData.banner || null,
                        banner_portrait: courseData.banner_portrait || null,
                        trailer_type: courseData.trailer_type || "file",
                        trailer_video: courseData.trailer_video || "",
                        trailer_video_file: null,
                        learning_outcomes: courseData.learning_outcomes || [],
                        price: courseData.price || null,
                        is_active: courseData.is_active,
                        is_upcoming: courseData.is_upcoming,
                        release_date: courseData.release_date || null,
                        category_ids: courseData.categories.map(c => c.id),
                        author_ids: courseData.authors.map(a => a.id),
                        theme_ids: courseData.themes?.map(t => t.id) || [],
                    })
                }
            } catch (error) {
                console.error("Failed to load data", error)
                toast({
                    title: "Ошибка",
                    description: "Не удалось загрузить данные курса",
                    variant: "destructive",
                })
            } finally {
                setIsLoading(false)
            }
        }
        initData()
    }, [slug, isEditMode, form, toast])

    const onSubmit = async (values: z.infer<typeof formSchema>) => {
        console.log("Form submitted with values:", values)
        setIsSaving(true)
        try {
            const formData = new FormData()
            formData.append("title", values.title)
            if (!isEditMode) {
                formData.append("slug", values.slug)
            }
            formData.append("description", values.description)
            formData.append("is_active", String(values.is_active))
            formData.append("is_upcoming", String(values.is_upcoming))
            if (values.is_upcoming && values.release_date) {
                formData.append("release_date", values.release_date)
            } else {
                formData.append("release_date", "")
            }
            if (values.banner instanceof File) {
                formData.append("banner", values.banner)
            }
            if (values.banner_portrait instanceof File) {
                formData.append("banner_portrait", values.banner_portrait)
            }

            // Handle trailer based on type
            formData.append("trailer_type", values.trailer_type)
            if (values.trailer_type === "file") {
                if (values.trailer_video_file instanceof File) {
                    formData.append("trailer_video_file", values.trailer_video_file)
                }
            } else if (values.trailer_type === "youtube") {
                if (values.trailer_video && typeof values.trailer_video === "string") {
                    formData.append("trailer_video", values.trailer_video)
                }
            }

            if (values.price) {
                formData.append("price", values.price)
            }

            // Handle learning_outcomes as JSON array
            if (values.learning_outcomes && values.learning_outcomes.length > 0) {
                formData.append("learning_outcomes", JSON.stringify(values.learning_outcomes))
            }

            // Handle arrays - Django DRF expects these to be sent multiple times
            values.category_ids.forEach(id => formData.append("category_ids", String(id)))
            values.author_ids.forEach(id => formData.append("author_ids", String(id)))
            values.theme_ids?.forEach(id => formData.append("theme_ids", String(id)))

            if (isEditMode && slug) {
                const updated = await courseService.updateCourse(slug, formData)
                setCourse(updated)
                toast({ title: "Успешно", description: "Курс успешно обновлен" })
            } else {
                const created = await courseService.createCourse(formData)
                toast({ title: "Успешно", description: "Курс успешно создан" })
                navigate(`/courses/${created.slug}/edit`)
            }
        } catch (error) {
            console.error(error)
            toast({
                title: "Ошибка",
                description: `Не удалось ${isEditMode ? "обновить" : "создать"} курс`,
                variant: "destructive",
            })
        } finally {
            setIsSaving(false)
        }
    }

    const handleLessonSuccess = async () => {
        // Refresh lessons
        if (slug) {
            const courseData = await courseService.getCourse(slug)
            setLessons(courseData.lessons || [])
        }
    }

    const handleDeleteLesson = async (id: number) => {
        try {
            await courseService.deleteLesson(id)
            toast({ title: "Успешно", description: "Урок удален" })
            handleLessonSuccess()
        } catch (error) {
            toast({ title: "Ошибка", description: "Не удалось удалить урок" })
        }
    }

    if (isLoading) {
        return <div className="p-8 text-center">Загрузка...</div>
    }

    return (
        <div className="pb-20">
            <div className="mb-6 flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate("/courses")}>
                    <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {isEditMode ? "Редактировать курс" : "Создать новый курс"}
                    </h1>
                    <p className="text-sm text-gray-500">
                        {isEditMode ? course?.title : "Заполните данные ниже"}
                    </p>
                </div>
                <div className="ml-auto flex gap-2">
                    {!isEditMode && (
                        <Button type="submit" form="course-form" disabled={isSaving}>
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Создание...
                                </>
                            ) : (
                                "Создать курс"
                            )}
                        </Button>
                    )}
                    {isEditMode && (
                        <Button
                            type="submit"
                            form="course-form"
                            disabled={isSaving}
                            onClick={() => {
                                console.log("Button clicked!")
                                console.log("Form errors:", form.formState.errors)
                                console.log("Form values:", form.getValues())
                            }}
                        >
                            {isSaving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Сохранение...
                                </>
                            ) : (
                                "Сохранить изменения"
                            )}
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                    <Tabs defaultValue="basic" className="space-y-6">
                        <TabsList>
                            <TabsTrigger value="basic">Основная информация</TabsTrigger>
                            <TabsTrigger value="relations">Связи</TabsTrigger>
                            {isEditMode && <TabsTrigger value="curriculum">Учебная программа</TabsTrigger>}
                        </TabsList>

                        <Form {...form}>
                            <form id="course-form" onSubmit={form.handleSubmit(onSubmit, (errors) => {
                                console.log("Form validation errors:", errors)
                            })}>
                                <TabsContent value="basic" className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Детали курса</CardTitle>
                                            <CardDescription>
                                                Основная информация о вашем курсе.
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="banner"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Баннер (альбомная ориентация)</FormLabel>
                                                        <FormControl>
                                                            <ImageUpload
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                label="Загрузить баннер"
                                                                className="w-full"
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Рекомендуемый размер: 1200x600px (для горизонтального отображения на десктопе)
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="banner_portrait"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Баннер (портретная ориентация)</FormLabel>
                                                        <FormControl>
                                                            <ImageUpload
                                                                value={field.value}
                                                                onChange={field.onChange}
                                                                label="Загрузить портретный баннер"
                                                                className="w-full"
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Рекомендуемый размер: 600x900px (для вертикального отображения на мобильных устройствах)
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="title"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Название</FormLabel>
                                                        <FormControl>
                                                            <Input {...field} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="slug"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Слаг</FormLabel>
                                                        <FormControl>
                                                            <Input {...field} placeholder="python-basics" maxLength={50} disabled={isEditMode} />
                                                        </FormControl>
                                                        <FormDescription>
                                                            {isEditMode
                                                                ? "Слаг не может быть изменен после создания"
                                                                : "Используйте только английские буквы, цифры и дефисы (макс. 50 символов)"}
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="description"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Описание</FormLabel>
                                                        <FormControl>
                                                            <Textarea {...field} className="min-h-[150px]" />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <div className="space-y-4 p-4 border rounded-lg">
                                                <h3 className="text-sm font-medium">Видео трейлера (необязательно)</h3>
                                                <FormField
                                                    control={form.control}
                                                    name="trailer_type"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Тип трейлера</FormLabel>
                                                            <FormControl>
                                                                <div className="flex gap-4">
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            value="file"
                                                                            checked={field.value === "file"}
                                                                            onChange={() => field.onChange("file")}
                                                                            className="w-4 h-4"
                                                                        />
                                                                        <span>Загрузить файл</span>
                                                                    </label>
                                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                                        <input
                                                                            type="radio"
                                                                            value="youtube"
                                                                            checked={field.value === "youtube"}
                                                                            onChange={() => field.onChange("youtube")}
                                                                            className="w-4 h-4"
                                                                        />
                                                                        <span>YouTube URL</span>
                                                                    </label>
                                                                </div>
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />

                                                {form.watch("trailer_type") === "file" ? (
                                                    <FormField
                                                        control={form.control}
                                                        name="trailer_video_file"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>Файл видео</FormLabel>
                                                                <FormControl>
                                                                    <VideoUpload
                                                                        value={field.value}
                                                                        onChange={field.onChange}
                                                                        label="Загрузить видео трейлера"
                                                                        maxSizeMB={100}
                                                                        className="w-full"
                                                                    />
                                                                </FormControl>
                                                                <FormDescription>
                                                                    Загрузите видео файл для предпросмотра курса (макс. 100MB)
                                                                </FormDescription>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                ) : (
                                                    <FormField
                                                        control={form.control}
                                                        name="trailer_video"
                                                        render={({ field }) => (
                                                            <FormItem>
                                                                <FormLabel>YouTube URL</FormLabel>
                                                                <FormControl>
                                                                    <Input
                                                                        {...field}
                                                                        value={field.value || ''}
                                                                        placeholder="https://youtube.com/watch?v=..."
                                                                        type="url"
                                                                    />
                                                                </FormControl>
                                                                <FormDescription>
                                                                    Введите полный URL YouTube видео (например: https://youtube.com/watch?v=abc123)
                                                                </FormDescription>
                                                                <FormMessage />
                                                            </FormItem>
                                                        )}
                                                    />
                                                )}
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="price"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Цена (USD, необязательно)</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={field.value || ''}
                                                                type="text"
                                                                placeholder="99.99"
                                                            />
                                                        </FormControl>
                                                        <FormDescription>
                                                            Введите цену курса в USD (например, 99.99). Оставьте пустым для бесплатных курсов.
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <div className="space-y-4 p-4 border rounded-lg">
                                                <div className="flex items-center justify-between mb-4">
                                                    <h3 className="text-sm font-medium">Результаты обучения (необязательно)</h3>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => append({ title: "", text: "" })}
                                                    >
                                                        <Plus className="mr-2 h-4 w-4" />
                                                        Добавить результат
                                                    </Button>
                                                </div>
                                                {fields.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500 text-sm">
                                                        Результаты обучения не добавлены. Нажмите "Добавить результат", чтобы добавить.
                                                    </div>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {fields.map((field, index) => (
                                                            <div key={field.id} className="space-y-3 p-4 border rounded-lg bg-gray-50 relative">
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    className="absolute top-2 right-2 h-6 w-6 text-gray-400 hover:text-red-600"
                                                                    onClick={() => remove(index)}
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </Button>
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`learning_outcomes.${index}.title`}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel>Название</FormLabel>
                                                                            <FormControl>
                                                                                <Input
                                                                                    {...field}
                                                                                    placeholder="например, Основы Python"
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                                <FormField
                                                                    control={form.control}
                                                                    name={`learning_outcomes.${index}.text`}
                                                                    render={({ field }) => (
                                                                        <FormItem>
                                                                            <FormLabel>Описание</FormLabel>
                                                                            <FormControl>
                                                                                <Textarea
                                                                                    {...field}
                                                                                    placeholder="Опишите, что изучат студенты..."
                                                                                    className="min-h-[100px]"
                                                                                />
                                                                            </FormControl>
                                                                            <FormMessage />
                                                                        </FormItem>
                                                                    )}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name="is_active"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                        <div className="space-y-0.5">
                                                            <FormLabel className="text-base">Статус активности</FormLabel>
                                                            <FormDescription>
                                                                Опубликуйте этот курс, чтобы сделать его видимым для пользователей.
                                                            </FormDescription>
                                                        </div>
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="is_upcoming"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                        <div className="space-y-0.5">
                                                            <FormLabel className="text-base">Предстоящий курс</FormLabel>
                                                            <FormDescription>
                                                                Отметьте этот курс как скоро выходящий/предстоящий.
                                                            </FormDescription>
                                                        </div>
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={field.value}
                                                                onCheckedChange={field.onChange}
                                                            />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                            {form.watch("is_upcoming") && (
                                                <FormField
                                                    control={form.control}
                                                    name="release_date"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Ожидаемая дата выпуска</FormLabel>
                                                            <FormControl>
                                                                <Input
                                                                    {...field}
                                                                    value={field.value || ''}
                                                                    placeholder="например, Март 2024, 2 квартал 2024"
                                                                />
                                                            </FormControl>
                                                            <FormDescription>
                                                                Введите ожидаемую дату выпуска для этого предстоящего курса.
                                                            </FormDescription>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            )}
                                        </CardContent>
                                    </Card>
                                </TabsContent>

                                <TabsContent value="relations" className="space-y-6">
                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Категории</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="category_ids"
                                                render={() => (
                                                    <FormItem>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                            {categories.map((item) => (
                                                                <FormField
                                                                    key={item.id}
                                                                    control={form.control}
                                                                    name="category_ids"
                                                                    render={({ field }) => {
                                                                        return (
                                                                            <FormItem
                                                                                key={item.id}
                                                                                className="flex flex-row items-start space-x-3 space-y-0"
                                                                            >
                                                                                <FormControl>
                                                                                    <Checkbox
                                                                                        checked={field.value?.includes(item.id)}
                                                                                        onCheckedChange={(checked) => {
                                                                                            return checked
                                                                                                ? field.onChange([...field.value, item.id])
                                                                                                : field.onChange(
                                                                                                    field.value?.filter(
                                                                                                        (value) => value !== item.id
                                                                                                    )
                                                                                                )
                                                                                        }}
                                                                                    />
                                                                                </FormControl>
                                                                                <FormLabel className="font-normal">
                                                                                    {item.title}
                                                                                </FormLabel>
                                                                            </FormItem>
                                                                        )
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>

                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Авторы</CardTitle>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="author_ids"
                                                render={() => (
                                                    <FormItem>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                            {authors.map((item) => (
                                                                <FormField
                                                                    key={item.id}
                                                                    control={form.control}
                                                                    name="author_ids"
                                                                    render={({ field }) => {
                                                                        return (
                                                                            <FormItem
                                                                                key={item.id}
                                                                                className="flex flex-row items-start space-x-3 space-y-0"
                                                                            >
                                                                                <FormControl>
                                                                                    <Checkbox
                                                                                        checked={field.value?.includes(item.id)}
                                                                                        onCheckedChange={(checked) => {
                                                                                            return checked
                                                                                                ? field.onChange([...field.value, item.id])
                                                                                                : field.onChange(
                                                                                                    field.value?.filter(
                                                                                                        (value) => value !== item.id
                                                                                                    )
                                                                                                )
                                                                                        }}
                                                                                    />
                                                                                </FormControl>
                                                                                <FormLabel className="font-normal">
                                                                                    {item.full_name}
                                                                                </FormLabel>
                                                                            </FormItem>
                                                                        )
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>

                                    <Card>
                                        <CardHeader>
                                            <CardTitle>Темы</CardTitle>
                                            <CardDescription>
                                                Выберите темы курса (необязательно).
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <FormField
                                                control={form.control}
                                                name="theme_ids"
                                                render={() => (
                                                    <FormItem>
                                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                            {themes.map((item) => (
                                                                <FormField
                                                                    key={item.id}
                                                                    control={form.control}
                                                                    name="theme_ids"
                                                                    render={({ field }) => {
                                                                        return (
                                                                            <FormItem
                                                                                key={item.id}
                                                                                className="flex flex-row items-start space-x-3 space-y-0"
                                                                            >
                                                                                <FormControl>
                                                                                    <Checkbox
                                                                                        checked={field.value?.includes(item.id)}
                                                                                        onCheckedChange={(checked) => {
                                                                                            return checked
                                                                                                ? field.onChange([...(field.value || []), item.id])
                                                                                                : field.onChange(
                                                                                                    (field.value || [])?.filter(
                                                                                                        (value) => value !== item.id
                                                                                                    )
                                                                                                )
                                                                                        }}
                                                                                    />
                                                                                </FormControl>
                                                                                <FormLabel className="font-normal">
                                                                                    {item.title}
                                                                                </FormLabel>
                                                                            </FormItem>
                                                                        )
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </TabsContent>
                            </form>
                        </Form>

                        {isEditMode && course && (
                            <TabsContent value="curriculum" className="space-y-6">
                                <Card>
                                    <CardHeader className="flex flex-row items-center justify-between">
                                        <div>
                                            <CardTitle>Учебная программа курса</CardTitle>
                                            <CardDescription>Управление уроками и видео.</CardDescription>
                                        </div>
                                        <Button size="sm" onClick={() => {
                                            setEditLesson(null)
                                            setIsLessonDialogOpen(true)
                                        }}>
                                            <Plus className="mr-2 h-4 w-4" />
                                            Добавить урок
                                        </Button>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-4">
                                            {lessons.length === 0 ? (
                                                <div className="text-center py-8 text-gray-500">
                                                    Уроков пока нет. Нажмите "Добавить урок", чтобы начать.
                                                </div>
                                            ) : (
                                                lessons.sort((a, b) => a.order - b.order).map((lesson) => (
                                                    <div key={lesson.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                                                        <div className="flex items-center gap-4">
                                                            <div className="bg-white p-2 rounded shadow-sm">
                                                                <GripVertical className="h-4 w-4 text-gray-400" />
                                                            </div>
                                                            <div>
                                                                <div className="font-medium flex items-center gap-2">
                                                                    <Badge variant="outline" className="mr-1">{lesson.order}</Badge>
                                                                    {lesson.title}
                                                                    {!lesson.is_active && <Badge variant="secondary" className="text-xs">Черновик</Badge>}
                                                                </div>
                                                                <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                                                                    {lesson.video ? (
                                                                        <span className="text-green-600 flex items-center gap-1">
                                                                            Видео: {lesson.video.title} ({lesson.video.duration_formatted})
                                                                        </span>
                                                                    ) : (
                                                                        <span className="text-red-400">Видео не прикреплено</span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Button variant="ghost" size="icon" onClick={() => {
                                                                setEditLesson(lesson)
                                                                setIsLessonDialogOpen(true)
                                                            }}>
                                                                <Edit2 className="h-4 w-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteLesson(lesson.id)}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        )}
                    </Tabs>
                </div>

                <div className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle>Публикация</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">Последнее обновление</span>
                                <span className="font-medium">{course?.updated_at ? new Date(course.updated_at).toLocaleDateString() : "Никогда"}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">Создано</span>
                                <span className="font-medium">{course?.created_at ? new Date(course.created_at).toLocaleDateString() : "Никогда"}</span>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            <LessonDialog
                lesson={editLesson}
                courseId={course?.id || 0}
                open={isLessonDialogOpen}
                onOpenChange={setIsLessonDialogOpen}
                onSuccess={handleLessonSuccess}
            />
        </div>
    )
}
